import * as React from 'react'
import { taskEither, array } from 'fp-ts'
import { pipe } from 'fp-ts/lib/function'
import { AutoComplete, Button } from 'antd'
import { DefaultOptionType } from 'rc-cascader'

type Props<T extends object> = {
  value?: T
  onSearch?: (_: string) => taskEither.TaskEither<unknown, T[]>
  onSelect?: (_: T) => void
  render?: (_: T) => string
  addOption?: { onClick: () => void; message: React.ReactNode }
  placeholder?: React.ReactNode
}

function renderFromProps<T extends object>({ render }: Props<T>) {
  if (!!render) {
    return render
  }

  return (x: T) => `${x}`
}

function unsafeToOption<T extends object>(i: unknown): T {
  const { option } = i as any
  return option as T
}

function AsyncSelect<T extends object>(props: Props<T>) {
  const { onSearch, onSelect, addOption, value } = props
  const [tOut, setTOut] = React.useState<NodeJS.Timeout | undefined>(undefined)
  const [options, setOptions] = React.useState<T[]>([])

  const search = (q: string) => {
    if (!onSearch) {
      return
    }
    if (!!tOut) {
      clearTimeout(tOut)
    }
    setTOut(setTimeout(pipe(onSearch(q), taskEither.map(setOptions)), 200))
  }
  const render = renderFromProps(props)

  return (
    <AutoComplete<string, DefaultOptionType>
      value={!!value ? render(value) : value}
      placeholder={props.placeholder}
      onFocus={() => search('')}
      onSearch={search}
      onSelect={
        !!onSelect
          ? (_: string, i: DefaultOptionType) => onSelect(unsafeToOption<T>(i))
          : undefined
      }
    >
      {!!addOption && (
        <AutoComplete.Option disabled value="" key="add">
          <Button
            style={{ width: '100%' }}
            type="ghost"
            onClick={e => {
              e.stopPropagation()
              e.preventDefault()
              addOption.onClick()
            }}
          >
            {addOption.message}
          </Button>
        </AutoComplete.Option>
      )}
      {pipe(
        options,
        array.map(o => {
          const v = render(o)
          return (
            <AutoComplete.Option value={v} key={v} option={o}>
              {v}
            </AutoComplete.Option>
          )
        })
      )}
    </AutoComplete>
  )
}

export default AsyncSelect
